import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { ConfigProvider, Input, Popover, Spin } from 'antd'
import { formatMessageNew } from '@/utils/common'
import { modalError } from '@/utils/modalUtils'
import { getEpcCycle } from '@/services/rfid/reports/epcCycleReport'
import EpcCycleFlow from '@/pages/rfid/reports/components/EpcCycleFlow'
import EpcCycleIcon, { Swoosh } from '@/pages/rfid/reports/components/EpcCycleIcons'
import { IoPill, stageName } from '@/pages/rfid/reports/components/EpcCyclePopover'
import {
  ACTION_CONFIG,
  MAX_INPUT,
  STAGE_CONFIG,
  allIndexes,
  colorVars,
  eventsOf,
  isGlobalQr,
  makeT,
  parseInputValues
} from '@/pages/rfid/reports/components/epcCycleUtils'
import '@/pages/rfid/reports/components/epcCycle.less'

const INPUT_EPC = 'EPC'
const INPUT_QR = 'QR'
const RETURN_TYPES = ['RETURN_2C', 'RETURN_2B']

/**
 * 查询后从 0 滚动到实际值
 */
const CountUp = ({ value, runKey }) => {
  const [shown, setShown] = useState(0)
  useEffect(() => {
    let frame
    const start = window.performance.now()
    const tick = now => {
      const p = Math.min(1, (now - start) / 900)
      setShown(Math.round(value * (1 - Math.pow(1 - p, 3))))
      if (p < 1) frame = window.requestAnimationFrame(tick)
    }
    frame = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(frame)
  }, [value, runKey])
  return <span>{shown}</span>
}

const renderTemplate = (template, values) =>
  String(template)
    .split(/(\{\w+\})/)
    .map((part, i) => {
      const m = /^\{(\w+)\}$/.exec(part)
      return m && values[m[1]] !== undefined ? <React.Fragment key={i}>{values[m[1]]}</React.Fragment> : part
    })

const EpcCycleReport = () => {
  const locale = useSelector(state => (state.commonStore.languageObj || {}).epcCycleReport) || {}
  const t = useMemo(() => makeT(locale), [locale])

  const [inputType, setInputType] = useState(INPUT_EPC)
  const [text, setText] = useState('')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState(null)
  const [requested, setRequested] = useState(0)
  const [runKey, setRunKey] = useState(0)
  const [activePop, setActivePop] = useState(null)
  const [legendHover, setLegendHover] = useState(false)
  const [legendPinned, setLegendPinned] = useState(false)
  const legendBtnRef = useRef(null)
  const pageContainerRef = useRef(null)
  const requestSeq = useRef(0)

  // Popover 容器限制（防止覆盖菜单）
  const getPopupContainer = () => pageContainerRef.current || document.body

  // 图例：悬停预览，点击固定，点击页面其他位置收起
  useEffect(() => {
    if (!legendPinned) return undefined
    const onDocDown = e => {
      const inButton = legendBtnRef.current && legendBtnRef.current.contains(e.target)
      const inPanel = e.target.closest && e.target.closest('.epc-legend-pop')
      if (!inButton && !inPanel) setLegendPinned(false)
    }
    document.addEventListener('mousedown', onDocDown)
    return () => document.removeEventListener('mousedown', onDocDown)
  }, [legendPinned])
  const legendOpen = legendHover || legendPinned

  const values = parseInputValues(text)

  // 检查是否有无效的 QR Code
  const hasInvalidQr = () => {
    if (inputType !== INPUT_QR) return false
    return values.some(v => v.includes('HTTPS') && !isGlobalQr(v))
  }

  const validate = list => {
    if (!list.length) return t('inputEmptyError')
    if (list.length > MAX_INPUT) return t('maxInputError')
    if (inputType === INPUT_QR && list.some(v => v.includes('HTTPS') && !isGlobalQr(v))) {
      return formatMessageNew('languageObj.defectiveReturnLocale.invalidQRCodeFormat')
    }
    return null
  }

  const onSearch = () => {
    const error = validate(values)
    if (error) {
      modalError({ content: error })
      return
    }
    const seq = ++requestSeq.current
    setActivePop(null)
    setLoading(true)
    getEpcCycle({ inputType, values })
      .then(res => {
        if (seq !== requestSeq.current) return
        setResults((res && res.resultData) || [])
        setRequested(values.length)
        setRunKey(k => k + 1)
      })
      .catch(() => {})
      .then(() => {
        if (seq === requestSeq.current) setLoading(false)
      })
  }

  const onClear = () => {
    requestSeq.current++
    setText('')
    setResults(null)
    setActivePop(null)
    setLoading(false)
  }

  const onChangeType = type => {
    setInputType(type)
    setText('')
  }

  const cards = results || []
  const found = cards.filter(c => c.status === 'NORMAL')
  const countEvents = kind =>
    found.reduce((n, c) => n + eventsOf(c.stages || [], allIndexes(c.stages || []), kind).length, 0)
  const kpis = [
    { key: 'found', n: found.length, of: results ? cards.length : null, hl: true },
    { key: 'stages', n: found.reduce((n, c) => n + (c.stages || []).length, 0) },
    {
      key: 'returns',
      n: found.reduce((n, c) => n + (c.stages || []).filter(s => RETURN_TYPES.includes(s.stageType)).length, 0)
    },
    { key: 'rework', n: countEvents('REWORK') },
    { key: 'reprint', n: countEvents('REPRINT') }
  ]

  const popClassName = 'epc-cycle-pop'

  const legend = (
    <div className="legend-panel">
      <div className="lg-row">
        <span className="lg-title">{t('legend.stages')}</span>
        {Object.keys(STAGE_CONFIG).map(type => (
          <span key={type} style={colorVars(STAGE_CONFIG[type].color)}>
            <i style={{ background: STAGE_CONFIG[type].color }} />
            {t(`stage.${type}`, null, type)}
            {STAGE_CONFIG[type].io ? <IoPill dirs={['IB', 'OB']} mini /> : null}
          </span>
        ))}
      </div>
      <div className="lg-row" style={colorVars('#595959')}>
        <span className="lg-title">{t('legend.direction')}</span>
        <span>
          <IoPill dirs={['IB', 'OB']} mini /> {t('legend.both')}
        </span>
        <span>
          <IoPill dirs={['IB']} mini /> {t('legend.ibOnly')}
        </span>
        <span>
          <IoPill dirs={['OB']} mini /> {t('legend.obOnly')}
        </span>
        <span className="muted">{t('legend.ioNote')}</span>
      </div>
      <div className="lg-row">
        <span className="lg-title">{t('legend.actions')}</span>
        {Object.keys(ACTION_CONFIG).map(kind => (
          <span
            key={kind}
            className={`act${kind === 'REPRINT' ? ' reprint' : ''}`}
            style={{ color: ACTION_CONFIG[kind].color }}
          >
            <EpcCycleIcon name={ACTION_CONFIG[kind].icon} />
            {t(`event.${ACTION_CONFIG[kind].localeKey}`)}
          </span>
        ))}
      </div>
    </div>
  )

  const statusTag = card => {
    switch (card.status) {
      case 'INVALID_EPC':
        return <span className="tag red">{t('statusInvalidEpc')}</span>
      case 'INVALID_QR':
        return <span className="tag red">{t('statusInvalidQr')}</span>
      case 'NO_GLOBAL_QR':
        return <span className="tag gray">{t('statusNoGlobalQr')}</span>
      case 'NO_ASSOCIATED_EPC':
        return <span className="tag gray">{formatMessageNew('languageObj.defectiveReturnLocale.noAssociatedEPC')}</span>
      default:
        return <span className="tag gray">{t('statusNoRecord')}</span>
    }
  }

  const renderCard = (card, i) => {
    const delay = i * 0.12
    if (card.status !== 'NORMAL') {
      return (
        <div className="epc-card" key={i} style={{ animationDelay: `${delay}s` }}>
          <div className="epc-head">
            <span className="epc-no">{card.epc || card.input}</span>
            {card.epc && card.input !== card.epc ? (
              <span className="meta">
                {t('inputTypeQr')}: <em>{card.input}</em>
              </span>
            ) : null}
            {statusTag(card)}
          </div>
          <div className="empty">{t('noRecordTip')}</div>
        </div>
      )
    }
    const stages = card.stages || []
    const all = allIndexes(stages)
    const last = stages[stages.length - 1]
    const related = card.relatedEpcs || []
    const metas = [
      [t('field.qrCode'), card.qrCode],
      [t('field.upc'), card.upc],
      [t('field.productCode'), card.productCode],
      [t('field.size'), card.size]
    ]
    return (
      <div className="epc-card" key={i} style={{ animationDelay: `${delay}s` }}>
        <div className="epc-head">
          <span className="epc-no">{card.epc}</span>
          {metas.map(([label, value]) => (
            <span className="meta" key={label}>
              {label}: <em>{value || '-'}</em>
            </span>
          ))}
          {related.length ? (
            <Popover
              trigger="click"
              rootClassName={popClassName}
              getPopupContainer={getPopupContainer}
              content={
                <div className="related-list">
                  {related.map(epc => (
                    <div key={epc} className="mono">
                      {epc}
                    </div>
                  ))}
                </div>
              }
            >
              <span className="tag gray clickable">{t('relatedEpcs', { n: related.length })}</span>
            </Popover>
          ) : null}
          {Object.keys(ACTION_CONFIG).map(kind => {
            const n = eventsOf(stages, all, kind).length
            return n ? (
              <span key={kind} className={`tag ${ACTION_CONFIG[kind].tag}`}>
                {t(`event.${ACTION_CONFIG[kind].localeKey}`)} ×{n}
              </span>
            ) : null
          })}
          {last ? (
            <div className="head-right">
              <span className="meta">{t('current')}:</span>
              <span className="tag orange">{stageName(t, last.stageType, last.directions || [])}</span>
            </div>
          ) : null}
        </div>
        <EpcCycleFlow
          t={t}
          card={card}
          cardIndex={i}
          baseDelay={delay}
          activePop={activePop}
          setActivePop={setActivePop}
          popClassName={popClassName}
          getPopupContainer={getPopupContainer}
        />
      </div>
    )
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#111111',
          colorLink: '#111111',
          borderRadius: 12
        }
      }}
    >
      <div ref={pageContainerRef} className="epc-cycle-page" style={{ overflow: 'hidden' }}>
        <section className="hero">
          <Swoosh className="swoosh-bg" />
          <div className="hero-top">
            <div>
              <div className="eyebrow">
                <Swoosh />
                {t('title')}
              </div>
              <h1>
                {t('hero.title1')} <span className="o">{t('hero.title2')}</span> {t('hero.title3')}{' '}
                <span className="v">{t('hero.title4')}</span> {t('hero.title5')}
              </h1>
            </div>
            <div className="kpis">
              {kpis.map(k => (
                <div className={`kpi${k.hl ? ' hl' : ''}`} key={k.key}>
                  <div className="n">
                    <CountUp value={k.n} runKey={runKey} />
                    {k.of != null ? <small>/{k.of}</small> : null}
                  </div>
                  <div className="l">{t(`kpi.${k.key}`)}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="search">
            <div className="search-col">
              <div className="search-top">
                <span className="field-label">{inputType === INPUT_QR ? t('inputTypeQr') : t('inputTypeEpc')}</span>
                <div className="radio-group">
                  {[INPUT_EPC, INPUT_QR].map(type => (
                    <span key={type} className={inputType === type ? 'on' : ''} onClick={() => onChangeType(type)}>
                      {type === INPUT_QR ? t('inputTypeQr') : t('inputTypeEpc')}
                    </span>
                  ))}
                </div>
              </div>
              <Input.TextArea
                className="epc-input"
                status={hasInvalidQr() ? 'error' : values.length > MAX_INPUT ? 'warning' : ''}
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder={inputType === INPUT_QR ? t('inputPlaceholderQr') : t('inputPlaceholderEpc')}
                autoSize={{ minRows: 2, maxRows: 6 }}
              />
              <div className="hint">
                <span>
                  {hasInvalidQr()
                    ? formatMessageNew('languageObj.defectiveReturnLocale.invalidQRCodeFormat')
                    : t('inputHint')}
                </span>
                <span className={`count${values.length > MAX_INPUT ? ' err' : ''}`}>
                  {values.length} / {MAX_INPUT}
                </span>
              </div>
            </div>
            <div className="btns">
              <button type="button" className="btn primary" onClick={onSearch} disabled={loading}>
                {t('search')} <span className="arr">→</span>
              </button>
              <button type="button" className="btn" onClick={onClear}>
                {t('clear')}
              </button>
            </div>
          </div>
        </section>

        <Spin spinning={loading} wrapperClassName="epc-spin-wrapper">
          {results ? (
            <div className="toolbar">
              <div className="summary">
                {renderTemplate(t('found'), { x: <b>{found.length}</b>, y: <b>{requested}</b> })}
                <Popover
                  open={legendOpen}
                  onOpenChange={setLegendHover}
                  trigger="hover"
                  placement="bottomLeft"
                  rootClassName={`${popClassName} epc-legend-pop`}
                  getPopupContainer={getPopupContainer}
                  content={legend}
                >
                  <button
                    type="button"
                    ref={legendBtnRef}
                    className={`legend-btn${legendPinned ? ' open' : ''}`}
                    title={t('legend.title')}
                    onClick={() => setLegendPinned(p => !p)}
                  >
                    *
                  </button>
                </Popover>
                <span className="muted">{t('clickTip')}</span>
              </div>
            </div>
          ) : null}
          <div className="result">{cards.map(renderCard)}</div>
        </Spin>
      </div>
    </ConfigProvider>
  )
}

export default EpcCycleReport
